import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, RotateCw, AlertCircle, Loader2 } from "lucide-react";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  category: string;
}

export default function CameraCapture({ onCapture, onClose, category }: CameraCaptureProps) {
  const { i18n } = useLingui();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const streamRef = useRef<MediaStream | null>(null);

  // Initialize camera
  useEffect(() => {
    let mounted = true;

    const initCamera = async () => {
      try {
        // Stop any existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        setLoading(true);
        setError(null);

        // Check for multiple cameras
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === "videoinput");
        setHasMultipleCameras(videoDevices.length > 1);

        // Request camera access
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        if (!mounted) {
          // Component unmounted, cleanup
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }

        streamRef.current = mediaStream;
        setStream(mediaStream);

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }

        setLoading(false);
      } catch (err) {
        if (!mounted) return;

        console.error("Camera error:", err);
        setLoading(false);

        const error = err as Error & { name: string };
        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
          setError(i18n._(t`Camera access denied. Please allow camera permissions in your browser settings.`));
        } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
          setError(i18n._(t`No camera found on this device.`));
        } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
          setError(i18n._(t`Camera is already in use by another application.`));
        } else {
          setError(i18n._(t`Failed to access camera. Please try again.`));
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      // Cleanup camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode, i18n]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const context = canvas.getContext("2d");
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob and then to File
    canvas.toBlob((blob) => {
      if (!blob) return;

      const timestamp = new Date().getTime();
      const filename = `camera-photo-${category}-${timestamp}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });

      onCapture(file);
      handleClose();
    }, "image/jpeg", 0.95);
  };

  const handleClose = () => {
    // Stop camera stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  const switchCamera = () => {
    // Stop current stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    // Toggle facing mode
    setFacingMode(prev => prev === "user" ? "environment" : "user");
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="relative w-full h-full md:h-auto md:max-w-4xl bg-gray-900 md:rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#166B93] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#166B93]/20 ring-2 ring-white/10">
                <Camera className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div className="flex flex-col">
                <h3 className="text-white font-bold text-lg md:text-xl font-nunito leading-tight drop-shadow-md">
                  <Trans>Take Photo</Trans>
                </h3>
                <p className="text-white/90 text-xs md:text-sm font-jakarta drop-shadow-md">
                  <Trans>Position your document or photo</Trans>
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleClose}
              className="bg-white/10 hover:bg-white/20 text-white border-0 backdrop-blur-md h-10 w-10 md:h-12 md:w-12 p-0 rounded-full shadow-lg transition-all duration-200"
            >
              <X className="h-5 w-5 md:h-6 md:w-6" />
            </Button>
          </div>
        </div>

        {/* Video/Error Content */}
        <div className="relative w-full h-full md:aspect-video bg-black flex items-center justify-center md:min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <div className="text-center space-y-4">
                <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#166B93]" />
                <p className="text-white text-lg">
                  <Trans>Initializing camera...</Trans>
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black p-8">
              <div className="text-center space-y-4 max-w-md">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-white text-lg font-semibold">
                  <Trans>Camera Error</Trans>
                </p>
                <p className="text-white/80 text-sm">{error}</p>
                <Button
                  type="button"
                  onClick={handleClose}
                  className="bg-[#166B93] hover:bg-[#145A7A] text-white"
                >
                  <Trans>Close</Trans>
                </Button>
              </div>
            </div>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${loading || error ? "hidden" : ""}`}
          />

          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Camera guide overlay */}
          {!loading && !error && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-8 md:inset-x-16 md:bottom-16 md:top-32 border-2 border-white/20 rounded-2xl">
                <div className="absolute top-0 left-0 w-8 h-8 md:w-12 md:h-12 border-t-[4px] border-l-[4px] border-[#CEF12F] rounded-tl-2xl shadow-sm"></div>
                <div className="absolute top-0 right-0 w-8 h-8 md:w-12 md:h-12 border-t-[4px] border-r-[4px] border-[#CEF12F] rounded-tr-2xl shadow-sm"></div>
                <div className="absolute bottom-0 left-0 w-8 h-8 md:w-12 md:h-12 border-b-[4px] border-l-[4px] border-[#CEF12F] rounded-bl-2xl shadow-sm"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 md:w-12 md:h-12 border-b-[4px] border-r-[4px] border-[#CEF12F] rounded-br-2xl shadow-sm"></div>
              </div>
            </div>
          )}

          {/* Controls - Moved to bottom overlay */}
          {!loading && !error && (
            <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-24 pb-10 px-6 md:px-8">
              <div className="flex items-center justify-center gap-8 md:gap-12">
                {/* Switch Camera Button */}
                {hasMultipleCameras && (
                  <Button
                    type="button"
                    onClick={switchCamera}
                    className="bg-white/10 hover:bg-white/20 text-white border-0 backdrop-blur-md h-12 w-12 md:h-14 md:w-14 p-0 rounded-full transition-all duration-200"
                  >
                    <RotateCw className="h-5 w-5 md:h-6 md:w-6" />
                  </Button>
                )}

                {/* Capture Button */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-[#CEF12F]/20 rounded-full blur-xl group-hover:bg-[#CEF12F]/40 transition-all duration-300"></div>
                  <Button
                    type="button"
                    onClick={handleCapture}
                    className="relative bg-[#CEF12F] hover:bg-[#B8E526] text-gray-900 h-20 w-20 md:h-24 md:w-24 p-0 rounded-full shadow-xl shadow-black/20 transform hover:scale-105 transition-all duration-200 border-4 border-black/10 background-clip-padding"
                  >
                    <Camera className="h-8 w-8 md:h-10 md:w-10" />
                  </Button>
                </div>

                {/* Spacer for symmetry */}
                {hasMultipleCameras && <div className="w-12 md:w-14"></div>}
              </div>

              <p className="text-center text-white/90 text-sm mt-6 font-medium drop-shadow-md">
                <Trans>Click the camera button to capture</Trans>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
